import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaGlobe, FaGreaterThan, FaLessThan,
  FaPlus, FaTimes, FaTrash, FaCopy,
  FaMagic, FaLayerGroup,
} from 'react-icons/fa';
import { MdTransform, MdFaceRetouchingNatural } from 'react-icons/md';
import './composition.css';
import Video from './Video';
import VideoClipsPanel from './VideoClipsPanel';
import ImageGrid from './ImageGrid';
import { toast } from 'react-toastify';
import Loader from '../../Components/Loader/Loader';
import { useAuthContext } from '../../hooks/UseAuthContext';
import api from '../../api';
import ConfirmationBox from '../../Components/Confirmation/ConfirmationBox';
import GetChannel from '../Channels/GetChannel';
import BgMusic from '../Voices/BgMusic';
import CaptionEditor from './CaptionEditor';
import CharacterSelector from './CharacterSelector';
import CharacterStudio from './CharacterStudio';
import IntroTeaserPanel from './IntroTeaserPanel';
import NarrationImagePanel from './NarrationImagePanel';
import TransitionBannerPanel from './TransitionBannerPanel';
import CanvasStudio from './CanvasStudio';
import './canvas-studio.css';
import {
  TEMPLATE_TYPES,
  parseBannerPayload,
} from './compositionTemplates';
import { getPresetById, normalizeTeaserConfig, parseTeaserPayload } from './teaserPresets';
import { loadSavedDesign } from './studioKit';
import { DEFAULT_BANNER_CONFIG, normalizeBannerConfig, normalizeBannerConfigForExport } from './bannerPresets';
import {
  validateClipTag,
  validateSegmentTag,
  findUntaggedClipSegments,
  hasIntroTagAnywhere,
} from './tagValidation';
import { getVideoSourceLabel } from './videoPlayback';
import { formatClipTimestamp, nudgeClipTimestamp, validateClipTimeUpdate, withFinishedClip } from './clipTimeUtils';
import { isRemoteShareLink } from './videoPlayback';
import SegmentAddMenu from './SegmentAddMenu';
import { clipTextFromTag, extractClipTagFromText, getClipSegmentSuggestions, isClipSegmentText } from './clipSegmentUtils';
import TimelineClipPreview, { ClipStrip, clipRangeSeconds } from './TimelineClipPreview';
import BulkClipLinkImport, { BulkClipLinkAnchorButton } from './BulkClipLinkImport.jsx';
import CompositionJsonImport from './CompositionJsonImport.jsx';
import { applyCompositionImportToIntro } from './compositionImportUtils';
import ClipHintDrawer from './ClipHintDrawer.jsx';
import {
  findClipByTag,
  clipHintForTag,
  previewRangeForClip,
  timelineCutsForTag,
} from './editHintsUtils';
import {
  parseBulkVideoLinks,
  collectClipTagsInSection,
  buildBulkImportBundle,
  mergeSectionVideos,
  mergeClipsTree,
  insertSegmentsAt,
} from './bulkLinkImportUtils';
import {
  ENABLE_BULK_LINK_FRONTEND_RESOLVE,
  resolveBulkImportUrls,
} from './bulkLinkResolve';
import './composition-fit.css'; // fit & scroll layer — keep last

const SECTION_COLORS = ['#7c3aed', '#2563eb', '#059669'];

const getClipToggleKey = (character, url) => `${character}::${url}`;

function Composition() {
  const { vDataId } = useParams();
  const [captionsEnabled, setCaptionsEnabled]     = useState(false);
  const [manualInput, setManualInput]             = useState('00:00:00.00');
  const [videoAddAction, setVideoAddAction]       = useState('video');
  const [isSavingStart, setIsSavingStart]         = useState(true);
  const [clipEditTarget, setClipEditTarget]       = useState(null);
  const [previewingClipIndex, setPreviewingClipIndex] = useState(null);
  const pendingUseAll                                 = useRef(null);
  const { user }                                   = useAuthContext();
  const [showConfirmation, setShowConfirmation]   = useState(false);
  const navigate                                   = useNavigate();
  const isSubmitting                               = useRef(false);
  const [narration, setNarration]                 = useState('');
  const [addMedia, setAddMedia]                   = useState('');
  const [existingUrl, setExistingUrl]             = useState('');
  const [niche, setNiche]                         = useState('');
  const [orientation, setOrientation]             = useState('');
  const [selectedMedia, setSelectedMedia]         = useState({ url: '', character: '' });
  const [title, setTitle]                         = useState('');
  const [active, setActive]                       = useState('images');
  const [loading, setLoading]                     = useState(false);
  const [sections, setSections]                   = useState(null);
  const [compositionLoadState, setCompositionLoadState] = useState('loading'); // loading | composing | ok | error
  const [compositionLoadMessage, setCompositionLoadMessage] = useState('');
  const [choosenSection, setChoosenSection]       = useState(1);
  const [sectionName, setSectionName]             = useState('intro');
  const [isChecked, setIsChecked]                 = useState([]);
  const [isClipChecked, setIsClipChecked]         = useState({});
  const [isOpen, setIsOpen]                       = useState(false);
  const videoRef                                   = useRef();
  const [channelData, setChannelData]             = useState(null);
  const [vidToggleIndex, setVidToggleIndex]       = useState(null);
  const [chooseChannel, setChooseChannel]         = useState(false);
  const [bgMusic, setBgMusic]                     = useState('');
  const [showCharacterStudio, setShowCharacterStudio] = useState(false);
  const [imageCache, setImageCache]               = useState({});

  // ── Intro Teaser (own tab + account default) ──────────────────
  const [teaserPresetId, setTeaserPresetId] = useState('breaking_news');
  const [introTeaserConfig, setIntroTeaserConfig] = useState(() => ({
    ...getPresetById('breaking_news').config,
  }));

  // ── Narration image modes ─────────────────────────────────────
  const [narrationImageMode, setNarrationImageMode] = useState('default');
  const [globalNarrationImage, setGlobalNarrationImage] = useState('');
  const [segmentNarrationImages, setSegmentNarrationImages] = useState({});

  // ── Feature toggles ───────────────────────────────────────────
  const [kenBurns, setKenBurns]                   = useState(true);
  const [storyboardAnimation, setStoryboardAnimation] = useState(false);
  const [avatar, setAvatar]                       = useState(false);
  const [transitions, setTransitions]             = useState(false);
  const [useTalkingHeads, setUseTalkingHeads]     = useState(false);
  // Canvas mode (Videos tab switch): off = the video is made exactly as before.
  const [canvasMode, setCanvasMode]               = useState(false);
  const [canvasConfigs, setCanvasConfigs]         = useState({ narration: null, clip: null });
  const handleCanvasConfig = useCallback((kind, cfg) => {
    setCanvasConfigs(prev => (prev[kind] === cfg ? prev : { ...prev, [kind]: cfg }));
  }, []);

  const [bannerPresetId, setBannerPresetId] = useState('blank');
  const [transitionBannerEnabled, setTransitionBannerEnabled] = useState(true);
  const [transitionBannerConfig, setTransitionBannerConfig] = useState(() => ({
    ...DEFAULT_BANNER_CONFIG,
  }));

  const handleKenBurns = () => {
    setKenBurns(true);
    setStoryboardAnimation(false);
  };

  const handleStoryboard = () => {
    setStoryboardAnimation(true);
    setKenBurns(false);
  };

  const [addImageData, setAddImageData] = useState({
    url: '', character: '', type: '', keyword: '', page: 1, number: null, bulkUrls: '',
  });
  const [bulkImportBusy, setBulkImportBusy] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [compositionImportOpen, setCompositionImportOpen] = useState(false);
  const [compositionImportBusy, setCompositionImportBusy] = useState(false);
  const [editHints, setEditHints] = useState(null);
  const [timelineHintOpenIndex, setTimelineHintOpenIndex] = useState(null);
  // Timeline clip preview: { index, playAll } of the [CLIP:tag] segment being previewed
  const [timelinePreview, setTimelinePreview] = useState(null);
  const [bulkImportPlacement, setBulkImportPlacement] = useState({ mode: 'end', index: 0 });

  const [addVideoData, setAddVideoData] = useState({
    url: '', character: '', title: '', clipJson: '',
  });

  const [selectedData, setSelectedData] = useState({
    intro:      { section: '', section_script: '', imageData: [], clips: [], section_characters: [], segment: [], section_images: [], section_videos: [] },
    body:       { section: '', section_script: '', imageData: [], clips: [], section_characters: [], segment: [], section_images: [], section_videos: [] },
    conclusion: { section: '', section_script: '', imageData: [], clips: [], section_characters: [], segment: [], section_images: [], section_videos: [] },
  });

  const [mediaPlannerOpen, setMediaPlannerOpen] = useState(() => {
    try { return JSON.parse(localStorage.getItem('mediaPlannerOpen')) || false; }
    catch { return false; }
  });

  useEffect(() => {
    localStorage.setItem('mediaPlannerOpen', JSON.stringify(mediaPlannerOpen));
  }, [mediaPlannerOpen]);

  useEffect(() => {
    setClipEditTarget(null);
  }, [selectedMedia.url, selectedMedia.character]);

  const [captionSettings, setCaptionSettings] = useState({
    position: 'bottom', fontSize: 52, fontFamily: 'Arial', fontWeight: 'bold',
    textColor: '#FFFFFF', highlightColor: '#FFD700', outlineColor: '#000000',
    backgroundColor: '#000000', backgroundOpacity: 0, outlineWidth: 0,
    alignment: 'center', wordsPerGroup: 3, style: 'karaoke',
  });

  const DEFAULT_AI_PROMPTS = [
    { id: 'ghibli',                  label: 'Studio Ghibli Animation' },
    { id: 'explanatory',             label: 'Explanatory Stick Figures' },
    { id: 'explanatory_dark',        label: 'Explanatory (Dark)' },
    { id: 'war',                     label: 'Wartime Realism' },
    { id: 'cinematic',               label: 'Cinematic' },
    { id: 'dark_story',              label: 'Dark Story Mood' },
    { id: 'luxury_motivation',       label: 'Motivation' },
    { id: 'chalk',                   label: 'Oil Paint' },
    { id: 'female_chalk',            label: 'Female Oil Paint' },
    { id: 'explanatory_illustrated', label: 'Explanatory Illustrated' },
    { id: 'fitness_video',           label: 'Fitness' },
  ];

  const MEDIA_TYPES = [
    { value: 'stock_video', label: 'Stock Video' },
    { value: 'stock_image', label: 'Stock Image' },
    { value: 'ai_image',    label: 'AI Image' },
  ];

  const defaultPhaseMedia = () => ({
    intro:  { mediaType: 'stock_video', prompt: '' },
    body:   { mediaType: 'stock_image', prompt: '' },
    finale: { mediaType: 'ai_image',    prompt: '' },
  });

  const [phasePlanner, setPhasePlanner] = useState({
    mode: 'phases', intro_min: 10, finale_min: 10,
    phase_media: defaultPhaseMedia(),
  });


  const phaseSummary = useMemo(() => {
    const pm  = phasePlanner?.phase_media || {};
    const fmt = (mt) => mt === 'stock_video' ? 'Stock Video' : mt === 'stock_image' ? 'Stock Image' : 'AI Image';
    return [
      `Intro: ${fmt(pm.intro?.mediaType  || 'stock_video')}`,
      `Body: ${fmt(pm.body?.mediaType    || 'stock_video')}`,
      `Finale: ${fmt(pm.finale?.mediaType || 'stock_video')}`,
      `Intro ${phasePlanner.intro_min}min · Finale ${phasePlanner.finale_min}min`,
    ].join('   ·   ');
  }, [phasePlanner]);

  const introTagActive = useMemo(
    () => hasIntroTagAnywhere({ sections, selectedData }),
    [sections, selectedData]
  );

  const setPhaseMinutes  = (key, val) => setPhasePlanner(p => ({ ...p, [key]: Math.max(0, Number(val) || 0) }));
  const setPhaseMediaType = (phase, mt) => setPhasePlanner(p => ({
    ...p,
    phase_media: {
      ...p.phase_media,
      [phase]: {
        mediaType:   mt,
        prompt:      mt === 'ai_image'        ? (p.phase_media[phase].prompt      || '') : undefined,
        characterId: mt === 'whisk_character' ? (p.phase_media[phase].characterId || null) : undefined,
      }
    },
  }));
  const setPhaseCharacter = (phase, charId) => setPhasePlanner(p => ({
    ...p,
    phase_media: { ...p.phase_media, [phase]: { ...p.phase_media[phase], characterId: charId } },
  }));
  const setPhasePrompt = (phase, v) => setPhasePlanner(p => ({
    ...p,
    phase_media: { ...p.phase_media, [phase]: { ...p.phase_media[phase], prompt: v } },
  }));

  // ── Saved banner + teaser designs ──────────────────────────────
  // On open: the account defaults (server, or this browser's copy offline).
  // When a channel is picked to generate: that channel's own designs, if any.
  const applyBannerDesign = useCallback((parsed) => {
    setBannerPresetId(parsed.presetId || 'custom');
    setTransitionBannerConfig(normalizeBannerConfig(parsed.config));
    if (parsed.enabled != null) setTransitionBannerEnabled(parsed.enabled !== false);
  }, []);

  const applyTeaserDesign = useCallback((parsed) => {
    setTeaserPresetId(parsed.presetId || 'custom');
    setIntroTeaserConfig(normalizeTeaserConfig(parsed.config));
  }, []);

  useEffect(() => {
    if (!user?.token) return undefined;
    let cancelled = false;
    (async () => {
      const [banner, teaser] = await Promise.all([
        loadSavedDesign({ type: TEMPLATE_TYPES.TRANSITION_BANNER, user, parse: parseBannerPayload }),
        loadSavedDesign({ type: TEMPLATE_TYPES.INTRO_TEASER, user, parse: parseTeaserPayload }),
      ]);
      if (cancelled) return;
      if (banner) applyBannerDesign(banner);
      if (teaser) applyTeaserDesign(teaser);
    })();
    return () => { cancelled = true; };
  }, [user, applyBannerDesign, applyTeaserDesign]);

  useEffect(() => {
    if (!channelData?._id || !user?.token) return undefined;
    let cancelled = false;
    (async () => {
      const [banner, teaser] = await Promise.all([
        loadSavedDesign({ type: TEMPLATE_TYPES.TRANSITION_BANNER, user, channelId: channelData._id, parse: parseBannerPayload }),
        loadSavedDesign({ type: TEMPLATE_TYPES.INTRO_TEASER, user, channelId: channelData._id, parse: parseTeaserPayload }),
      ]);
      if (cancelled) return;
      if (banner) applyBannerDesign(banner);
      if (teaser) applyTeaserDesign(teaser);
    })();
    return () => { cancelled = true; };
  }, [channelData?._id, user, applyBannerDesign, applyTeaserDesign]);

  useEffect(() => {
    if (!vDataId || !user?.token) return undefined;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 120;
    let timerId = null;

    const applyVideoData = (data) => {
      setTitle(data.title);
      setNiche(data.niche);
      setOrientation(data?.orientation);
      setSections(data.sections);
      setNarration(data.narration_url);
      setEditHints(data.editHints || null);
      if (Array.isArray(data.sections)) {
        setSelectedData(prev => {
          const updated = { ...prev };
          data.sections.forEach(section => {
            const key = section.section;
            if (!updated[key]) return;
            updated[key] = {
              ...updated[key],
              section: section.section,
              section_script: section.section_script,
              section_characters: section.section_characters || [],
              segment: section.segment || [],
              section_images: section.section_images || [],
              section_videos: section.section_videos || [],
              clips: section.clips || [],
              imageData: section.imageData || [],
            };
          });
          return updated;
        });
        if (data.sections.length === 1) {
          setChoosenSection(1);
          setSectionName(data.sections[0]?.section || 'intro');
        }
      }
    };

    const scheduleRetry = (message, { silent = false } = {}) => {
      if (cancelled || attempts >= maxAttempts) {
        if (!silent) {
          setCompositionLoadState('error');
          const notFound = `No composition found for ID "${vDataId}". It may have been deleted or the link is wrong — open a draft from Drafts.`;
          setCompositionLoadMessage(message || notFound);
          toast.error(message || notFound);
        }
        return;
      }
      attempts += 1;
      if (!silent) {
        setCompositionLoadState('composing');
        setCompositionLoadMessage(message || 'Composing your script…');
      }
      timerId = setTimeout(fetchVideoData, 2000);
    };

    const fetchVideoData = () => {
      if (cancelled) return;
      if (attempts === 0) {
        setCompositionLoadState('loading');
        setCompositionLoadMessage('');
      }

      api.get(`/video_data/${vDataId}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      }).then(r => {
        if (cancelled) return;
        const data = r.data;
        const stillComposing = data.status === 'composing';
        const hasSections = Array.isArray(data.sections) && data.sections.length > 0;

        if (stillComposing) {
          if (hasSections) {
            applyVideoData(data);
            setCompositionLoadState('ok');
            setCompositionLoadMessage('');
            scheduleRetry('Updating sections in the background…', { silent: true });
          } else {
            scheduleRetry('Composing your script…');
          }
          return;
        }

        applyVideoData(data);
        setCompositionLoadState('ok');
        setCompositionLoadMessage('');
      }).catch(e => {
        if (cancelled) return;
        const status = e.response?.status;
        if (status === 404 && attempts < maxAttempts) {
          scheduleRetry('Waiting for composition to be created…');
          return;
        }
        console.error('Error fetching video data', e);
        setCompositionLoadState('error');
        const msg = e.response?.data?.error || e.response?.data?.message;
        if (status === 404) {
          const notFound = `No composition found for ID "${vDataId}". It may have been deleted or the link is wrong — open a draft from Drafts.`;
          setCompositionLoadMessage(notFound);
          toast.error(notFound);
        } else if (status === 400) {
          const badId = msg || `Invalid composition ID "${vDataId}".`;
          setCompositionLoadMessage(badId);
          toast.error(badId);
        } else if (status === 401) {
          setCompositionLoadMessage('Sign in again to open this composition.');
          toast.error('Sign in again to open this composition.');
        } else {
          setCompositionLoadMessage(msg || 'Could not load composition from the server.');
          toast.error(msg || 'Could not load composition from the server.');
        }
      });
    };

    fetchVideoData();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [vDataId, user?.token]);

  useEffect(() => {
    if (sections?.length > 0) {
      setSelectedData(prev => {
        const updated = { ...prev };
        sections.forEach(section => {
          const key = section.section;
          if (!updated[key]) return;
          updated[key] = {
            ...updated[key],
            section:             section.section,
            section_script:      section.section_script,
            section_characters:  section.section_characters,
            segment:             section.segment,
            section_images:      section.section_images      || updated[key].section_images,
            section_videos:      section.section_videos      || updated[key].section_videos,
            imageData:           section.imageData           || updated[key].imageData,
            clips: prev[key].clips?.length > 0 ? prev[key].clips : (section.clips || []),
          };
        });
        return updated;
      });
    }
  }, [sections]);

  useEffect(() => {
    if (sections?.[choosenSection - 1]?.section_images) {
      sections[choosenSection - 1].section_images.forEach(imgGroup => {
        (imgGroup.images || []).forEach(image => {
          if (!imageCache[image]) {
            const img = new Image();
            img.src    = image;
            img.onload = () => setImageCache(prev => ({ ...prev, [image]: true }));
          }
        });
      });
    }
  }, [choosenSection, sections, imageCache]);

  const addCharacter = () => {
    sections[choosenSection - 1].section_characters.push('');
    setSections([...sections]);
  };

  const handleCharacterChange = (index, value) => {
    setSections(prev => {
      const updated = [...prev];
      updated[choosenSection - 1].section_characters =
        updated[choosenSection - 1].section_characters.map((c, i) => i === index ? value : c);
      return updated;
    });
  };

  const removeCharacter = (index) => {
    setSections(prev => {
      const updated = [...prev];
      const chars   = [...updated[choosenSection - 1].section_characters];
      chars.splice(index, 1);
      updated[choosenSection - 1] = { ...updated[choosenSection - 1], section_characters: chars };
      return updated;
    });
  };

  const handleToggle     = (i) => setIsChecked(prev     => { const n = [...prev]; n[i] = !n[i]; return n; });

  const handleClipToggle = (clipIdx) => {
    const key = getClipToggleKey(selectedMedia.character, selectedMedia.url);
    setIsClipChecked(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [clipIdx]: !prev[key]?.[clipIdx] },
    }));
  };

  const insertSegmentAfter = useCallback((index, newSeg) => {
    setSections(prev => prev.map((section, sIdx) => {
      if (sIdx !== choosenSection - 1) return section;
      const segs = Array.isArray(section.segment) ? section.segment : [];
      const nextSegs = [...segs.slice(0, index + 1), newSeg, ...segs.slice(index + 1)];
      return {
        ...section,
        segment: nextSegs,
        section_script: nextSegs.map(s => s.text || '').join(' '),
      };
    }));
    const showTag = !!(newSeg.tag || '').trim();
    setIsChecked(prev => [...prev.slice(0, index + 1), showTag, ...prev.slice(index + 1)]);
  }, [choosenSection]);

  const addBlankSegment = useCallback((index) => {
    insertSegmentAfter(index, { character: '', text: '', tag: '' });
  }, [insertSegmentAfter]);

  const addGenericClipSegment = useCallback((index) => {
    const id = `clip-${Date.now()}`;
    insertSegmentAfter(index, { character: '', text: clipTextFromTag(id), tag: '' });
    setIsChecked(prev => {
      const next = [...prev];
      if (next.length > index + 1) next[index + 1] = true;
      return next;
    });
  }, [insertSegmentAfter]);

  const addClipSegmentByTag = useCallback((index, tag) => {
    const trimmed = (tag || '').trim();
    if (!trimmed) return;

    const newIndex = index + 1;
    const result = validateSegmentTag({
      tag: trimmed,
      sectionKey: sectionName,
      sections,
      segmentIndex: newIndex,
    });
    if (!result.ok) {
      toast.error(result.message);
      return;
    }

    const sec = sections[choosenSection - 1];
    const chars = sec?.section_characters || [];
    const afterSeg = sec?.segment?.[index];
    const character = afterSeg?.character || chars[0] || '';

    insertSegmentAfter(index, {
      character,
      text: clipTextFromTag(trimmed),
      tag: trimmed,
    });
  }, [sectionName, sections, choosenSection, insertSegmentAfter]);

  const handleBulkClipLinkImport = useCallback(async ({ character, linksText, tagPrefix, placement }) => {
    const urls = parseBulkVideoLinks(linksText);
    if (!urls.length) {
      toast.error('Add at least one http(s) link.');
      return;
    }

    const sec = sections[choosenSection - 1];
    if (!sec?.section_characters?.includes(character)) {
      toast.error('Character not in this section.');
      return;
    }

    if (!ENABLE_BULK_LINK_FRONTEND_RESOLVE) {
      toast.error('Bulk link resolve is disabled in bulkLinkResolve.js');
      return;
    }

    setBulkImportBusy(true);
    try {
      const resolved = await resolveBulkImportUrls(api, urls, user.token, {
        onItemError: (i, linkUrl, err) => {
          const msg = err?.response?.data?.error || err?.message || 'failed';
          toast.error(`Link ${i + 1}: ${msg}`);
        },
      });

      if (!resolved.length) return;

      const sectionKey = sectionName;
      const usedTags = collectClipTagsInSection(selectedData[sectionKey]?.clips);
      (sec.segment || []).forEach(seg => {
        const t = (seg.tag || '').trim();
        if (t) usedTags.add(t);
      });

      const { videos, segments: newSegs } = buildBulkImportBundle(
        resolved,
        character,
        tagPrefix,
        usedTags,
      );
      if (!newSegs.length) {
        toast.error('Could not build clips from those links.');
        return;
      }

      const insertAt = (() => {
        const existing = sec.segment || [];
        const mode = placement?.mode || 'end';
        const anchor = Number.isFinite(placement?.index) ? placement.index : existing.length - 1;
        if (mode === 'end') return existing.length;
        if (mode === 'after') return Math.min(existing.length, Math.max(0, anchor + 1));
        return Math.min(existing.length, Math.max(0, anchor));
      })();

      setSections(prev => prev.map((section, sIdx) => {
        if (sIdx !== choosenSection - 1) return section;
        const segs = insertSegmentsAt(section.segment, newSegs, placement);
        return {
          ...section,
          segment: segs,
          section_script: segs.map(s => s.text || '').join(' '),
          section_videos: mergeSectionVideos(section.section_videos, character, videos),
        };
      }));

      setSelectedData(prev => {
        const section = prev[sectionKey] || {};
        return {
          ...prev,
          [sectionKey]: {
            ...section,
            clips: mergeClipsTree(section.clips, character, videos),
          },
        };
      });

      setIsChecked(prev => {
        const toggles = newSegs.map(s => !!(s.tag || '').trim());
        return [...prev.slice(0, insertAt), ...toggles, ...prev.slice(insertAt)];
      });

      toast.success(
        `Imported ${newSegs.length} full-length clip(s) — timeline + Videos tab (tags ${tagPrefix}1…).`,
      );
    } finally {
      setBulkImportBusy(false);
    }
  }, [sections, choosenSection, sectionName, selectedData, user.token]);

  const handleCompositionJsonImport = useCallback((compositionJson, warnings = []) => {
    if (sectionName !== 'intro') {
      toast.error('Composition JSON import is Intro tab only.');
      return;
    }
    setCompositionImportBusy(true);
    try {
      const {
        clipTags,
        warnings: w2,
        editHints: hints,
        sectionsForSave,
      } = applyCompositionImportToIntro(compositionJson, {
        sections,
        setSections,
        setSelectedData,
      });
      if (hints) setEditHints(hints);
      setChoosenSection(1);
      setSectionName('intro');
      setActive('videos');
      const sourceUrl = hints?.timebase
        || compositionJson.sources?.[0]?.url
        || '';
      const importChar = (compositionJson.character || '').trim();
      if (sourceUrl && importChar) {
        setSelectedMedia({ url: sourceUrl.trim(), character: importChar });
      }
      const segs = compositionJson.sections?.intro?.segment || [];
      setIsChecked(segs.map(() => true));
      [...warnings, ...w2].forEach(msg => toast.warn(msg));
      if (vDataId && user?.token && sectionsForSave?.length) {
        api.put(
          '/update_sections',
          {
            video_id: vDataId,
            section_data: sectionsForSave,
            ...(hints ? { editHints: hints } : {}),
          },
          { headers: { Authorization: `Bearer ${user.token}` } },
        ).catch(() => {
          toast.warn('Imported locally — restart backend to persist cut hints on save.');
        });
      }
      toast.success(
        hints
          ? `Intro import: ${segs.length} segment(s). Click Hint on tagged clips or timeline rows.`
          : `Intro import: ${segs.length} timeline segment(s), tags ${clipTags.join(', ') || '—'}.`,
      );
      setCompositionImportOpen(false);
    } catch (err) {
      toast.error(err.message || 'Composition import failed');
    } finally {
      setCompositionImportBusy(false);
    }
  }, [sectionName, sections, vDataId, user?.token]);

  const deleteSegment = (index) => {
    if (sections[choosenSection - 1]?.segment.length <= 1) return;
    setSections(prev => prev.map((section, sIdx) => {
      if (sIdx !== choosenSection - 1) return section;
      const segs = Array.isArray(section.segment) ? section.segment : [];
      return { ...section, segment: [...segs.slice(0, index), ...segs.slice(index + 1)] };
    }));
    setIsChecked(prev => [...prev.slice(0, index), ...prev.slice(index + 1)]);
  };

  const handleImageSelect = useCallback((imageUrl, character) => {
    setSelectedData(prevData => {
      const updatedData = { ...prevData };
      if (sectionName === 'intro' || sectionName === 'body') {
        const target  = updatedData[sectionName];
        if (!Array.isArray(target.imageData)) target.imageData = [];
        const existIdx = target.imageData.findIndex(item => item.characterName === character);
        if (existIdx !== -1) {
          const charImgs = target.imageData[existIdx].images;
          target.imageData[existIdx].images = charImgs.includes(imageUrl)
            ? charImgs.filter(img => img !== imageUrl)
            : [...charImgs, imageUrl];
        } else {
          target.imageData.push({ characterName: character, images: [imageUrl] });
        }
      }
      return updatedData;
    });
  }, [sectionName]);

  const onClipSave = useCallback((startTime, endTime, character) => {
    setSelectedData(prevData => {
      const sectionKey   = sectionName;
      const updatedClips = [...(prevData[sectionKey]?.clips || [])];
      const charIdx      = updatedClips.findIndex(c => c.characterName === character);
      const newClip      = { startTime, endTime, mute: true };
      if (charIdx !== -1) {
        const charData  = { ...updatedClips[charIdx] };
        const vids      = [...charData.videos];
        const vidIdx    = vids.findIndex(v => v.url === selectedMedia.url);
        if (vidIdx !== -1) {
          const video    = { ...vids[vidIdx] };
          const vidclips = [...video.vidclips];
          if (endTime === null) vidclips.push(newClip);
          else vidclips[vidclips.length - 1] = { ...vidclips[vidclips.length - 1], endTime };
          vids[vidIdx] = { ...video, vidclips };
        } else { vids.push({ url: selectedMedia.url, vidclips: [newClip] }); }
        charData.videos      = vids;
        updatedClips[charIdx] = charData;
      } else {
        updatedClips.push({ characterName: character, videos: [{ url: selectedMedia.url, vidclips: [newClip] }] });
      }
      return { ...prevData, [sectionKey]: { ...prevData[sectionKey], clips: updatedClips } };
    });
  }, [sectionName, selectedMedia]);

  const deleteClip = useCallback((character, url, videoIndex, clipIndex) => {
    setSelectedData(prevData => {
      const sectionKey    = sectionName;
      const updatedSection = { ...prevData[sectionKey] };
      const updatedClips   = [...(updatedSection.clips || [])];
      const charIdx        = updatedClips.findIndex(c => c.characterName === character);
      if (charIdx === -1) return prevData;
      const charData    = { ...updatedClips[charIdx] };
      const updatedVids = [...charData.videos];
      const video       = { ...updatedVids[videoIndex] };
      const updatedVidclips = [...video.vidclips];
      updatedVidclips.splice(clipIndex, 1);
      if (video.vidclips[clipIndex]?.endTime === null) videoRef.current?.resetClipState();
      if (updatedVidclips.length > 0) updatedVids[videoIndex] = { ...video, vidclips: updatedVidclips };
      else updatedVids.splice(videoIndex, 1);
      if (updatedVids.length > 0) { charData.videos = updatedVids; updatedClips[charIdx] = charData; }
      else updatedClips.splice(charIdx, 1);
      return { ...prevData, [sectionKey]: { ...updatedSection, clips: updatedClips } };
    });
  }, [sectionName]);

  const updateClipTime = useCallback((character, url, videoIndex, clipIndex, field, timeString) => {
    setSelectedData(prevData => {
      const sectionKey = sectionName;
      const updatedClips = [...(prevData[sectionKey]?.clips || [])];
      const charIdx = updatedClips.findIndex(c => c.characterName === character);
      if (charIdx === -1) return prevData;

      const charData = { ...updatedClips[charIdx] };
      const vids = [...charData.videos];
      const video = { ...vids[videoIndex] };
      const vidclips = [...(video.vidclips || [])];
      const clip = vidclips[clipIndex];
      if (!clip) return prevData;

      const result = validateClipTimeUpdate(clip.startTime, clip.endTime, field, timeString);
      if (!result.ok) {
        toast.error(result.error);
        return prevData;
      }

      vidclips[clipIndex] = { ...clip, [field]: result.value };
      vids[videoIndex] = { ...video, vidclips };
      charData.videos = vids;
      updatedClips[charIdx] = charData;

      return { ...prevData, [sectionKey]: { ...prevData[sectionKey], clips: updatedClips } };
    });
  }, [sectionName]);

  const muteClip = useCallback((character, url, videoIndex, clipIndex) => {
    setSelectedData(prevData => {
      const sectionKey    = sectionName;
      const updatedSection = { ...prevData[sectionKey] };
      const updatedClips   = [...(updatedSection.clips || [])];
      const charIdx        = updatedClips.findIndex(c => c.characterName === character);
      if (charIdx === -1) return prevData;
      const charData    = { ...updatedClips[charIdx] };
      const updatedVids = [...charData.videos];
      const video       = { ...updatedVids[videoIndex] };
      video.vidclips    = video.vidclips.map((vc, i) => i === clipIndex ? { ...vc, mute: !vc.mute } : vc);
      updatedVids[videoIndex] = video;
      charData.videos      = updatedVids;
      updatedClips[charIdx] = charData;
      return { ...prevData, [sectionKey]: { ...updatedSection, clips: updatedClips } };
    });
  }, [sectionName]);

  const handleAddTag = (index, e) => {
    const tagValue = e.target.value;

    const result = validateSegmentTag({
      tag: tagValue,
      sectionKey: sectionName,
      sections,
      selectedData,
      segmentIndex: index,
    });

    if (!result.ok) {
      toast.error(result.message);
      return;
    }

    setSections(prev => {
      const updated = [...prev];
      updated[choosenSection - 1].segment[index].tag = tagValue;
      return updated;
    });
  };

  const handleAddClipTag = (character, url, clipIndex, e) => {
    const tagValue = e.target.value;

    const currentClip = selectedData[sectionName]?.clips
      ?.find(c => c.characterName === character)
      ?.videos?.find(v => v.url === url)
      ?.vidclips?.[clipIndex];

    const result = validateClipTag({
      tag: tagValue,
      sectionKey: sectionName,
      sections,
      selectedData,
      exclude: {
        character,
        url,
        clipIndex,
        previousTag: currentClip?.tag || '',
      },
    });

    if (!result.ok) {
      toast.error(result.message);
      return;
    }

    setSelectedData(prevData => {
      const sectionKey    = sectionName;
      const updatedSection = { ...prevData[sectionKey] };
      const updatedClips   = [...(updatedSection.clips || [])];
      const charIdx        = updatedClips.findIndex(c => c.characterName === character);
      if (charIdx === -1) return prevData;
      const charData    = { ...updatedClips[charIdx] };
      const updatedVids = [...charData.videos];
      const vidIdx      = updatedVids.findIndex(v => v.url === url);
      if (vidIdx === -1) return prevData;
      const updatedVideo       = { ...updatedVids[vidIdx] };
      updatedVideo.vidclips    = updatedVideo.vidclips.map((vc, i) => i === clipIndex ? { ...vc, tag: tagValue } : vc);
      updatedVids[vidIdx]      = updatedVideo;
      charData.videos          = updatedVids;
      updatedClips[charIdx]    = charData;
      return { ...prevData, [sectionKey]: { ...updatedSection, clips: updatedClips } };
    });
  };

  const handleAddEffect = (character, url, clipIndex, e) => {
    const effect = e.target.value;
    setSelectedData(prevData => {
      const sectionKey    = sectionName;
      const updatedSection = { ...prevData[sectionKey] };
      const updatedClips   = [...(updatedSection.clips || [])];
      const charIdx        = updatedClips.findIndex(c => c.characterName === character);
      if (charIdx === -1) return prevData;
      const charData    = { ...updatedClips[charIdx] };
      const updatedVids = [...charData.videos];
      const vidIdx      = updatedVids.findIndex(v => v.url === url);
      if (vidIdx === -1) return prevData;
      const updatedVideo    = { ...updatedVids[vidIdx] };
      updatedVideo.vidclips = updatedVideo.vidclips.map((vc, i) => i === clipIndex ? { ...vc, effect } : vc);
      updatedVids[vidIdx]   = updatedVideo;
      charData.videos       = updatedVids;
      updatedClips[charIdx] = charData;
      return { ...prevData, [sectionKey]: { ...updatedSection, clips: updatedClips } };
    });
  };

  /**
   * Set (cropSpec) or remove (cropSpec = null) the crop on clips of one source video.
   * target: array of clip indices, or legacy 'all' | 'single' (last clip).
   */
  const applyCropToClips = (character, url, cropSpec, target) => {
    setSelectedData(prevData => {
      const sectionKey     = sectionName;
      const updatedSection = { ...prevData[sectionKey] };
      const updatedClips   = [...(updatedSection.clips || [])];
      const charIdx        = updatedClips.findIndex(c => c.characterName === character);
      if (charIdx === -1) return prevData;
      const charData = updatedClips[charIdx];
      const vidIdx   = (charData.videos || []).findIndex(v => v.url === url);
      if (vidIdx === -1) return prevData;

      const video    = charData.videos[vidIdx];
      const vidclips = video.vidclips || [];
      const indices  = new Set(
        Array.isArray(target)
          ? target
          : target === 'all'
            ? vidclips.map((_, i) => i)
            : (vidclips.length ? [vidclips.length - 1] : [])
      );

      const nextVidclips = vidclips.map((clip, i) => {
        if (!indices.has(i)) return clip;
        if (cropSpec) return { ...clip, crop: cropSpec };
        const { crop, ...rest } = clip;
        return rest;
      });

      const videos = [...charData.videos];
      videos[vidIdx] = { ...video, vidclips: nextVidclips };
      updatedClips[charIdx] = { ...charData, videos };
      return { ...prevData, [sectionKey]: { ...updatedSection, clips: updatedClips } };
    });

    const count = Array.isArray(target) ? target.length : null;
    const label = count == null
      ? (target === 'all' ? 'all clips' : 'last clip')
      : count === 1 ? `clip ${target[0] + 1}` : `${count} clips`;
    toast.success(cropSpec ? `Crop applied to ${label}` : `Crop removed from ${label}`);
  };

  const handleManualChange = (e) => {
    let value = e.target.value.replace(/[^\d:.]/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    setManualInput(value);
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!videoRef.current) return;
    const result = await videoRef.current.processManualEntry(manualInput);
    if (result?.success === 'Start time saved') setIsSavingStart(false);
    else if (result?.success === 'End time saved') setIsSavingStart(true);
    if (result?.error) toast.error(result.error);
  };

  const handleDurationReady = useCallback((seconds) => {
    const pending = pendingUseAll.current;
    if (!pending || !(seconds > 0.05)) return;
    const end = formatClipTimestamp(seconds);
    if (!end) return;
    pendingUseAll.current = null;
    setSelectedData(prev => {
      const section = prev[pending.sectionName] || {};
      return {
        ...prev,
        [pending.sectionName]: {
          ...section,
          clips: withFinishedClip(section.clips, pending.character, pending.url, '00:00:00.00', end),
        },
      };
    });
    toast.success(`Using the full clip · 00:00:00.00 → ${end}`);
  }, []);

  const handleUseAll = useCallback(() => {
    const seconds = videoRef.current?.getDurationSeconds?.() || 0;
    if (!(seconds > 0.05)) {
      toast.error('Wait for the video to load, then click Use all.');
      return;
    }
    const end = formatClipTimestamp(seconds);
    if (!end || !selectedMedia.url || !selectedMedia.character) return;
    setSelectedData(prev => {
      const section = prev[sectionName] || {};
      return {
        ...prev,
        [sectionName]: {
          ...section,
          clips: withFinishedClip(section.clips, selectedMedia.character, selectedMedia.url, '00:00:00.00', end),
        },
      };
    });
    videoRef.current?.resetClipState?.();
    setIsSavingStart(true);
    toast.success(`Using the full clip · 00:00:00.00 → ${end}`);
  }, [sectionName, selectedMedia]);

  const handleClipRangeEnd = useCallback(() => {
    setPreviewingClipIndex(null);
  }, []);

  const runClipRangePreview = useCallback((clipIdx, start, end) => {
    if (!start || !end) {
      toast.error('Set a start and end time before previewing');
      return;
    }
    if (previewingClipIndex === clipIdx) {
      videoRef.current?.stopClipRangePreview?.();
      setPreviewingClipIndex(null);
      return;
    }
    const result = videoRef.current?.playClipRange?.(start, end);
    if (!result?.ok) {
      setPreviewingClipIndex(null);
      toast.error(result?.error || 'Could not preview this clip');
      return;
    }
    setPreviewingClipIndex(clipIdx);
  }, [previewingClipIndex]);

  const handlePreviewClip = useCallback((clipIdx) => {
    const charClips = selectedData[sectionName]?.clips?.find(c => c.characterName === selectedMedia.character);
    const videos = charClips?.videos || [];
    let clip = videos.find(v => v.url === selectedMedia.url)?.vidclips?.[clipIdx];
    const base = editHints?.timebase;
    const baseVideo = base ? videos.find(v => v.url === base) : null;
    if (baseVideo && clip?.tag) {
      const onBase = baseVideo.vidclips?.findIndex(
        c => (c.tag || '').trim() === (clip.tag || '').trim(),
      );
      if (onBase >= 0) clip = baseVideo.vidclips[onBase];
    } else if (!clip && baseVideo?.vidclips?.[clipIdx]) {
      clip = baseVideo.vidclips[clipIdx];
    }

    const hint = clipHintForTag(editHints, clip?.tag);
    const { start, end } = previewRangeForClip(clip, hint);
    let previewIdx = clipIdx;
    if (baseVideo && clip?.tag) {
      const onBase = baseVideo.vidclips?.findIndex(
        c => (c.tag || '').trim() === (clip.tag || '').trim(),
      );
      if (onBase >= 0) previewIdx = onBase;
    }

    if (base && selectedMedia.url !== base) {
      toast.info('Switching to source video for this clip hint');
      const char = sections?.[0]?.section_characters?.[0]
        || selectedData.intro?.section_characters?.[0]
        || selectedMedia.character
        || '';
      setSelectedMedia({ url: base, character: char });
      window.setTimeout(() => runClipRangePreview(previewIdx, start, end), 450);
      return;
    }

    runClipRangePreview(previewIdx, start, end);
  }, [
    sectionName,
    selectedData,
    selectedMedia,
    editHints,
    sections,
    runClipRangePreview,
  ]);

  const handleSeekToTimestamp = useCallback((timeString) => {
    if (!videoRef.current?.seekToTimestamp) {
      toast.error('Select a video to seek');
      return;
    }
    const result = videoRef.current.seekToTimestamp(timeString);
    if (!result?.ok) toast.error(result?.error || 'Could not seek to that time');
  }, []);

  const applyClipTimeFromVideo = useCallback((timeString) => {
    if (!clipEditTarget || !selectedMedia.character || !selectedMedia.url) return;

    const charClips = selectedData[sectionName]?.clips?.find(c => c.characterName === selectedMedia.character);
    const vidIdx = charClips?.videos?.findIndex(v => v.url === selectedMedia.url) ?? -1;
    if (vidIdx < 0) return;

    const clip = charClips.videos[vidIdx].vidclips?.[clipEditTarget.clipIndex];
    if (!clip) return;

    const result = validateClipTimeUpdate(
      clip.startTime,
      clip.endTime,
      clipEditTarget.field,
      timeString,
    );
    if (!result.ok) {
      toast.error(result.error);
      return;
    }

    updateClipTime(
      selectedMedia.character,
      selectedMedia.url,
      vidIdx,
      clipEditTarget.clipIndex,
      clipEditTarget.field,
      result.value,
    );
    setClipEditTarget(null);
    videoRef.current?.releaseTimestampEdit?.();
  }, [clipEditTarget, selectedData, sectionName, selectedMedia, updateClipTime]);

  const handleActivateClipField = useCallback((clipIdx, field) => {
    videoRef.current?.releaseTimestampEdit?.();
    setPreviewingClipIndex(null);

    const charClips = selectedData[sectionName]?.clips?.find(c => c.characterName === selectedMedia.character);
    const vidIdx = charClips?.videos?.findIndex(v => v.url === selectedMedia.url) ?? -1;
    const clip = vidIdx >= 0 ? charClips?.videos?.[vidIdx]?.vidclips?.[clipIdx] : null;
    const currentTime = clip?.[field] || null;

    setClipEditTarget({ clipIndex: clipIdx, field, previewTime: currentTime, originalTime: currentTime });

    if (currentTime && videoRef.current?.seekToTimestamp) {
      videoRef.current.seekToTimestamp(currentTime);
    }
  }, [selectedData, sectionName, selectedMedia]);

  const handleSeekFromCutHint = useCallback((timeString) => {
    if (!timeString) return;
    const base = editHints?.timebase;
    if (base && selectedMedia.url && selectedMedia.url !== base) {
      toast.info('Switching to source video for this cut');
      const char = sections?.[0]?.section_characters?.[0]
        || selectedData.intro?.section_characters?.[0]
        || '';
      setSelectedMedia({ url: base, character: char });
      setActive('videos');
      setTimeout(() => handleSeekToTimestamp(timeString), 400);
      return;
    }
    if (base && !selectedMedia.url) {
      const char = sections?.[0]?.section_characters?.[0] || '';
      setSelectedMedia({ url: base, character: char });
      setActive('videos');
      setTimeout(() => handleSeekToTimestamp(timeString), 400);
      return;
    }
    handleSeekToTimestamp(timeString);
  }, [editHints, selectedMedia.url, sections, selectedData, handleSeekToTimestamp]);

  useEffect(() => {
    if (active !== 'timeline') setTimelinePreview(null);
  }, [active]);

  useEffect(() => {
    setTimelinePreview(null);
  }, [sectionName]);

  const openClipInVideos = useCallback((loc) => {
    if (!loc) return;
    setTimelinePreview(null);
    setSelectedMedia({ url: loc.url, character: loc.character });
    setActive('videos');
  }, []);

  const focusVideosClipByTag = useCallback((tag, run) => {
    const loc = findClipByTag(selectedData, sectionName, tag, editHints?.timebase);
    if (!loc) {
      toast.error(`Tag "${tag}" not found on Videos tab`);
      return null;
    }
    const url = editHints?.timebase || loc.url;
    const needsSwitch = selectedMedia.url !== url || selectedMedia.character !== loc.character;
    if (needsSwitch) {
      setSelectedMedia({ url, character: loc.character });
      setActive('videos');
      window.setTimeout(() => run(loc), 400);
      return loc;
    }
    run(loc);
    return loc;
  }, [selectedData, sectionName, editHints, selectedMedia]);

  const handleActivateClipFieldForTag = useCallback((tag, field) => {
    focusVideosClipByTag(tag, (loc) => {
      handleActivateClipField(loc.clipIndex, field);
    });
  }, [focusVideosClipByTag, handleActivateClipField]);

  const handleSaveClipField = useCallback(() => {
    if (!clipEditTarget?.previewTime) return;
    applyClipTimeFromVideo(clipEditTarget.previewTime);
  }, [clipEditTarget, applyClipTimeFromVideo]);

  const handleCancelClipField = useCallback(() => {
    const original = clipEditTarget?.originalTime;
    setClipEditTarget(null);
    setPreviewingClipIndex(null);
    videoRef.current?.releaseTimestampEdit?.();
    if (original && videoRef.current?.seekToTimestamp) {
      videoRef.current.seekToTimestamp(original);
    }
  }, [clipEditTarget]);

  const handleClipEditPlayhead = useCallback((timeString) => {
    setClipEditTarget(prev => {
      if (!prev || prev.previewTime === timeString) return prev;
      return { ...prev, previewTime: timeString };
    });
  }, []);

  const handleNudgeClipTime = useCallback((clipIdx, field, deltaSec) => {
    const charClips = selectedData[sectionName]?.clips?.find(c => c.characterName === selectedMedia.character);
    const clip = charClips?.videos?.find(v => v.url === selectedMedia.url)?.vidclips?.[clipIdx];
    const live = clipEditTarget?.clipIndex === clipIdx && clipEditTarget?.field === field
      ? clipEditTarget.previewTime
      : null;
    const base = live || videoRef.current?.getPlayheadTimestamp?.() || clip?.[field];
    const next = nudgeClipTimestamp(base, deltaSec);
    if (!next) return;
    const result = videoRef.current?.seekToTimestamp?.(next);
    if (result && !result?.ok) toast.error(result?.error || 'Could not seek the video');
  }, [clipEditTarget, sectionName, selectedData, selectedMedia]);

  const copyClipObjectToClipboard = (videoData) => {
    navigator.clipboard.writeText(JSON.stringify(videoData, null, 2))
      .then(() => toast.success('Clip data copied!'))
      .catch(() => toast.error('Failed to copy.'));
  };

  const handleScriptChange = (e) => {
    const newScript = e.target.value;
    setSections(prev => {
      const updated = [...prev];
      updated[choosenSection - 1] = { ...updated[choosenSection - 1], section_script: newScript };
      return updated;
    });
  };

  const updateSectionData = async () => {
    setLoading(true);
    const sectionDataToSend = sections.map(section => {
      const copy = JSON.parse(JSON.stringify(section));
      const key  = section.section;
      if (selectedData[key]) {
        copy.clips     = selectedData[key].clips     || [];
        copy.imageData = selectedData[key].imageData || [];
      }
      return copy;
    });
    try {
      await api.put('/update_sections',
        {
          video_id: vDataId,
          section_data: sectionDataToSend,
          ...(editHints ? { editHints } : {}),
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      toast.success('Changes saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update section');
    } finally { setLoading(false); }
  };

  const mediaPlanPayload = {
    mode:       'phases',
    intro_sec:  Math.max(0, (phasePlanner.intro_min  || 0) * 60),
    finale_sec: Math.max(0, (phasePlanner.finale_min || 0) * 60),
    phase_media: {
      intro:  { mediaType: phasePlanner.phase_media.intro.mediaType,  prompt: phasePlanner.phase_media.intro.prompt  || undefined, characterId: phasePlanner.phase_media.intro.characterId  || undefined },
      body:   { mediaType: phasePlanner.phase_media.body.mediaType,   prompt: phasePlanner.phase_media.body.prompt   || undefined, characterId: phasePlanner.phase_media.body.characterId   || undefined },
      finale: { mediaType: phasePlanner.phase_media.finale.mediaType, prompt: phasePlanner.phase_media.finale.prompt || undefined, characterId: phasePlanner.phase_media.finale.characterId || undefined },
    },
  };

  const runPreGenerateChecks = () => {
    const untaggedClips = findUntaggedClipSegments(sections);

    if (untaggedClips.length > 0) {
      const summary = untaggedClips
        .slice(0, 3)
        .map(p => `${p.section} segment ${p.segmentIndex + 1}`)
        .join(', ');

      toast.error(
        `Untagged clip section(s): ${summary}${untaggedClips.length > 3 ? '…' : ''}. Tag every [CLIP:…] segment before generating.`
      );
      return false;
    }

    if (narrationImageMode === 'global' && !globalNarrationImage.trim()) {
      toast.error('Global narration image mode is on, but no image was uploaded or provided.');
      return false;
    }

    if (narrationImageMode === 'per_segment') {
      const missing = [];
      (sections || []).forEach(section => {
        (section.segment || []).forEach((seg, idx) => {
          const text = (seg.text || '').trim();
          const isClip = text.startsWith('[CLIP:') && text.endsWith(']');
          if (isClip) return;
          const url = segmentNarrationImages?.[section.section]?.[idx];
          if (!url?.trim()) missing.push(`${section.section} segment ${idx + 1}`);
        });
      });
      if (missing.length > 0) {
        toast.error(`Missing narration image for: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '…' : ''}`);
        return false;
      }
    }

    return true;
  };

  const finalizeVideo = () => {
    setLoading(true);
    api.post('/finalize-video', {
      data: selectedData, narration_url: narration, vidDataId: vDataId, title,
      channelId: channelData._id, avatarImage: channelData.backgroundImage,
      backgroundVideo: channelData.backgroundVideo, channelLogo: channelData.logo,
      voice: channelData.voice, backgroundMusic: channelData.background_music,
      niche, bgMusic, mediaPlan: mediaPlanPayload,
      kenBurns, storyboardAnimation, orientation, avatar,
      captionsEnabled, captionSettings,
      useTalkingHeads,
      // canvas mode (normal videos only): one canvas for the narration parts, one
      // for the clips; the backend plays the channel banner between them
      canvas: canvasMode && !useTalkingHeads && (canvasConfigs.narration || canvasConfigs.clip)
        ? { enabled: true, narration_layout: canvasConfigs.narration, clip_layout: canvasConfigs.clip }
        : undefined,
      introTeaser: {
        presetId: teaserPresetId,
        config: normalizeTeaserConfig(introTeaserConfig),
      },
      narrationImages: {
        mode: narrationImageMode,
        globalImage: narrationImageMode === 'global' ? globalNarrationImage : undefined,
        perSegment: narrationImageMode === 'per_segment' ? segmentNarrationImages : undefined,
      },
      transition_config: {
        enabled: transitions,
        types:   ['zoom', 'translation'],
        max_pct: 0.15,
        max_sec: 0.6,
      },
      transitionBanner: {
        enabled: transitionBannerEnabled || (canvasMode && !useTalkingHeads),
        presetId: bannerPresetId,
        config: normalizeBannerConfigForExport(transitionBannerConfig, orientation),
      },
    }, {
      headers: { Authorization: `Bearer ${user.token}` },
    }).then(() => {
      setLoading(false);
      if (useTalkingHeads) {
        toast.success('Audio batches generating — check Telegram for download links!');
        setTimeout(() => navigate('/processing-drafts'), 800);
      } else {
        toast.success("Video generation started! You'll be notified when ready.");
        setTimeout(() => navigate('/processing-drafts'), 800);
      }
    }).catch(error => {
      setLoading(false);
      console.error('Error finalizing video:', error);
      toast.error('Failed to generate video.');
    });
  };

  useEffect(() => {
    if (sections) {
      setAddImageData(prev => ({ ...prev, character: sections[choosenSection - 1].section_characters[0] || '' }));
      setAddVideoData(prev => ({ ...prev, character: sections[choosenSection - 1].section_characters[0] || '' }));
    }
  }, [choosenSection, sections]);

  useEffect(() => {
    if (sections) {
      const updated = sections.map(section => ({
        ...section,
        section_script: section.segment.map(seg => seg.text).join(' '),
      }));
      if (JSON.stringify(sections) !== JSON.stringify(updated)) setSections(updated);
    }
  }, [sections]);

  const ensureTargetSection = (prev) => {
    const updated = Array.isArray(prev) ? [...prev] : [];
    const idx     = Math.max(0, (choosenSection || 1) - 1);
    const key     = sectionName || updated[idx]?.section || 'intro';
    if (!updated[idx]) {
      updated[idx] = { section: key, section_script: '', section_characters: [], segment: [{ character: '', text: '' }], section_images: [], section_videos: [], imageData: [], clips: [] };
    } else {
      updated[idx] = { ...updated[idx], section_images: Array.isArray(updated[idx].section_images) ? [...updated[idx].section_images] : [] };
    }
    return { updated, idx };
  };

  const addUrlsToSection = (urls) => {
    setSections(prev => {
      const { updated, idx } = ensureTargetSection(prev);
      const section  = updated[idx];
      const charIdx  = section.section_images.findIndex(x => x.character === addImageData.character);
      if (charIdx !== -1) {
        const existing = new Set(section.section_images[charIdx].images || []);
        const newUnique = urls.filter(u => !existing.has(u));
        section.section_images[charIdx] = {
          ...section.section_images[charIdx],
          images: [...newUnique, ...(section.section_images[charIdx].images || [])],
        };
      } else {
        section.section_images.push({ character: addImageData.character, images: urls });
      }
      updated[idx] = section;
      return updated;
    });
    setTimeout(() => urls.forEach(u => handleImageSelect(u, addImageData.character)), 0);
  };

  const manualImageAdd = (e) => {
    e.preventDefault();
    if (!addImageData.character) { toast.error('No character selected.'); return; }
    if (isSubmitting.current) return;
    isSubmitting.current = true;

    if (addImageData.type === 'single-image') {
      if (!addImageData.url) { toast.error('Please fill all fields.'); isSubmitting.current = false; return; }
      addUrlsToSection([addImageData.url]);
      toast.success('Image added & auto-selected');
      setAddImageData({ url: '', character: addImageData.character, type: '', keyword: '', page: 1, number: null, bulkUrls: '' });
      isSubmitting.current = false; return;
    }
    if (addImageData.type === 'multiple-image') {
      if (!addImageData.keyword) { toast.error('Provide a search keyword.'); isSubmitting.current = false; return; }
      if (!addImageData.number)  { toast.error('Enter number of images.'); isSubmitting.current = false; return; }
      setLoading(true);
      api.post('/fetch_images', { keyword: addImageData.keyword, n: addImageData.number })
        .then(res => { addUrlsToSection((res?.data || []).filter(Boolean)); toast.success('Images fetched & selected'); setLoading(false); })
        .catch(() => { toast.error('Failed to fetch images'); setLoading(false); });
      setAddImageData({ url: '', character: addImageData.character, type: '', keyword: '', page: 1, number: null, bulkUrls: '' });
      isSubmitting.current = false; return;
    }
    if (addImageData.type === 'bulk-image') {
      const urls = (addImageData.bulkUrls || '').split(',').map(u => u.trim()).filter(Boolean);
      if (!urls.length) { toast.error('Provide valid URLs.'); isSubmitting.current = false; return; }
      addUrlsToSection(urls);
      toast.success(`${urls.length} images added`);
      setAddImageData({ url: '', character: addImageData.character, type: '', keyword: '', page: 1, number: null, bulkUrls: '' });
      isSubmitting.current = false; return;
    }
    toast.error('Choose an image type.'); isSubmitting.current = false;
  };

  const manualVideoAdd = async (e, options = {}) => {
    e.preventDefault();
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    const { url, character, title: vidTitle, clipJson } = addVideoData;
    if (!character) { toast.error('Select a character.'); isSubmitting.current = false; return; }

    let videoToAdd = null;
    let usedFullClip = false;
    if (clipJson) {
      try {
        const parsed = JSON.parse(clipJson);
        if (!parsed.url || !Array.isArray(parsed.vidclips)) { toast.error('Invalid clip JSON.'); isSubmitting.current = false; return; }
        videoToAdd = { ...parsed, title: vidTitle || parsed.title || 'Untitled' };
      } catch { toast.error('Invalid JSON format.'); isSubmitting.current = false; return; }
    } else {
      if (!url || !vidTitle) { toast.error('Fill all required fields.'); isSubmitting.current = false; return; }
      let vidclips = [];
      if (options.useAll && isRemoteShareLink(url)) {
        try {
          const res = await api.post('/dreamface/resolve-preview', { url });
          const duration = Number(res.data?.duration || 0);
          const end = duration > 0.05 ? formatClipTimestamp(duration) : null;
          if (end) {
            vidclips = [{ startTime: '00:00:00.00', endTime: end, mute: true }];
            usedFullClip = true;
          }
        } catch {
          toast.error('Could not read that link yet. Open the video and click Use all.');
        }
      }
      videoToAdd = { url, title: vidTitle, vidclips };
    }

    setSections(prev => {
      const updated = [...prev];
      const section = updated[choosenSection - 1];
      const charExists  = section.section_videos.find(v => v.Character === character);
      const charInSection = section.section_characters.includes(character);
      if (!charInSection) { toast.error('Character not in this section.'); isSubmitting.current = false; return prev; }

      if (charExists) {
        const vidIdx = charExists.videos.findIndex(v => v.url === videoToAdd.url);
        if (vidIdx !== -1) {
          if (clipJson) { charExists.videos[vidIdx] = videoToAdd; toast.success('Video overridden with pasted JSON.'); }
          else { setExistingUrl(videoToAdd.url); setTimeout(() => setExistingUrl(''), 3000); toast.error('URL already exists.'); isSubmitting.current = false; return prev; }
        } else { charExists.videos.unshift(videoToAdd); toast.success('Video added.'); }
      } else { section.section_videos.push({ Character: character, videos: [videoToAdd] }); toast.success('Video added.'); }
      return updated;
    });

    setSelectedData(prev => {
      const updated    = { ...prev };
      const sectionKey = sectionName;
      const existingChar = updated[sectionKey].clips.find(c => c.characterName === character);
      if (existingChar) {
        const vidExists = existingChar.videos.find(v => v.url === videoToAdd.url);
        if (!vidExists) existingChar.videos.push(videoToAdd);
        else if (clipJson) existingChar.videos = existingChar.videos.map(v => v.url === videoToAdd.url ? videoToAdd : v);
      } else { updated[sectionKey].clips.push({ characterName: character, videos: [videoToAdd] }); }
      return updated;
    });

    setSelectedMedia({ url: videoToAdd.url, character });
    if (options.useAll && !usedFullClip && !clipJson) {
      pendingUseAll.current = { url: videoToAdd.url, character, sectionName };
      setTimeout(() => {
        if (pendingUseAll.current?.url !== videoToAdd.url) return;
        const seconds = videoRef.current?.getDurationSeconds?.() || 0;
        if (seconds > 0.05) handleDurationReady(seconds);
      }, 600);
    } else if (usedFullClip) {
      const clip = videoToAdd.vidclips[0];
      toast.success(`Using the full clip · ${clip.startTime} → ${clip.endTime}`);
    }
    setAddVideoData({ url: '', character: addVideoData.character, title: '', clipJson: '' });
    setAddMedia('');
    isSubmitting.current = false;
  };

  const handleConfirm = (confirmed) => {
    setShowConfirmation(false);
    if (confirmed) finalizeVideo();
  };

  if (compositionLoadState === 'error') {
    return (
      <div className="composition-load-error">
        <h2>Could not open composition</h2>
        <p>{compositionLoadMessage || 'Could not load this composition.'}</p>
        {vDataId && (
          <p className="composition-load-error__id">
            ID: <code>{vDataId}</code>
          </p>
        )}
        <div className="composition-load-error__actions">
          <button type="button" className="btn-save" onClick={() => window.location.reload()}>Retry</button>
          <button type="button" className="composition-load-error__secondary" onClick={() => navigate('/drafts')}>Go to Drafts</button>
        </div>
      </div>
    );
  }

  if (compositionLoadState !== 'ok' || !sections) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, minHeight: '100vh', background: '#09090b', color: '#aaa' }}>
        <Loader />
        {(compositionLoadMessage || compositionLoadState === 'composing') && (
          <p style={{ maxWidth: 320, textAlign: 'center', fontSize: 14 }}>
            {compositionLoadMessage || 'Loading composition…'}
          </p>
        )}
      </div>
    );
  }

  const sectionIndex = Math.min(Math.max(0, choosenSection - 1), sections.length - 1);
  const currentSection = sections[sectionIndex];
  const activeClipTagToggles = isClipChecked[getClipToggleKey(selectedMedia.character, selectedMedia.url)] || {};

  return (
    <>
      <div className={`composition-panel ${isOpen ? 'open' : 'close'}`}>

        {/* ─── SIDE PANEL ──────────────────────────────────── */}
        <div className="side-panel">
          <div className="panel-header">
            <select onChange={e => setActive(e.target.value)} value={active}>
              <option value="images">🖼 Images</option>
              <option value="teaser">📺 Intro Teaser</option>
              <option value="banner">🎬 Transition Banner</option>
              <option value="videos">🎬 Videos</option>
              <option value="scripts">📝 Scripts</option>
              <option value="timeline">⏱ Timeline</option>
              <option value="characters">👤 Characters</option>
              <option value="captions">💬 Captions</option>
              <option value="bgMusic">🎵 Background Music</option>
            </select>
          </div>

          <div className="panel-items">
            <div className="section-side">
              <div className="section-side-fixed">
                {sections.map((section, index) => (
                  <div key={section.section || index} className="section-numbers">
                    <p
                      onClick={() => { setChoosenSection(index + 1); setSectionName(section.section); }}
                      title={section.section}
                      style={{
                        background:  choosenSection === index + 1 ? SECTION_COLORS[index % SECTION_COLORS.length] : undefined,
                        borderColor: choosenSection === index + 1 ? SECTION_COLORS[index % SECTION_COLORS.length] : undefined,
                        color:       choosenSection === index + 1 ? '#fff' : undefined,
                      }}
                    >
                      {index + 1}
                    </p>
                  </div>
                ))}
                {(active === 'videos' || active === 'images') && (
                  <div className="add-section-media" onClick={() => setAddMedia(active === 'videos' ? 'video' : 'image')}>
                    <FaPlus />
                  </div>
                )}
              </div>
            </div>

            <div className="section-data">
              {active === 'videos' && currentSection?.section_videos?.map((videoData, index) => (
                <div key={index}>
                  <div className="character-link-container">
                    <div className="charcter-link">
                      <FaGlobe />
                      <a href={`https://www.google.com/search?q=${videoData.Character}`} target="_blank" rel="noreferrer">
                        {videoData.Character}
                      </a>
                    </div>
                  </div>
                  <div className="video-link-side-panel">
                    {videoData.videos.map((data, vIdx) => (
                      <div
                        key={vIdx}
                        onClick={() => setSelectedMedia({ url: data.url, character: videoData.Character })}
                        className={existingUrl === data.url ? 'existing-url' : ''}
                        style={{ borderLeft: selectedMedia.url === data.url ? '2px solid var(--accent)' : '2px solid transparent' }}
                      >
                        <h3>{getVideoSourceLabel(data.url).toUpperCase()}</h3>
                        <p>{data.title}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ─── MOBILE SLIDER ────────────────────────────────── */}
        <div className="slider-control">
          {isOpen
            ? <div className="slider-open"  onClick={() => setIsOpen(false)}><FaLessThan /></div>
            : <div className="slider-close" onClick={() => setIsOpen(true)}><FaGreaterThan /></div>
          }
        </div>

        {/* ─── MAIN PANEL ───────────────────────────────────── */}
        <div className="compostion-main-panel">

          {/* Phase planner — shown for every draft, single- or multi-section */}
          {mediaPlannerOpen ? (
            <div className="phase-planner-open">
              <div className="phase-planner-header">Phase Planner</div>
              <div className="phase-duration-controls">
                <div className="duration-input-group">
                  <label>Intro (min)</label>
                  <input type="number" min={0} value={phasePlanner.intro_min}
                    onChange={e => setPhaseMinutes('intro_min', e.target.value)} />
                </div>
                <div className="duration-input-group">
                  <label>Finale (min)</label>
                  <input type="number" min={0} value={phasePlanner.finale_min}
                    onChange={e => setPhaseMinutes('finale_min', e.target.value)} />
                </div>
              </div>

              <div className="phase-media-grid">
                {['intro', 'body', 'finale'].map(phase => (
                  <div key={phase} className="phase-media-row">
                    <div className="phase-label">{phase}</div>
                    <div className="phase-media-type">
                      <select value={phasePlanner.phase_media[phase].mediaType} onChange={e => setPhaseMediaType(phase, e.target.value)}>
                        {MEDIA_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    {phasePlanner.phase_media[phase].mediaType === 'whisk_character' && (
                      <div className="phase-media-prompt">
                        <label className="character-select-label">Select Character</label>
                        <CharacterSelector
                          selectedCharacterId={phasePlanner.phase_media[phase].characterId}
                          onCharacterSelect={charId => setPhaseCharacter(phase, charId)}
                        />
                        <button className="create-character-btn" onClick={() => setShowCharacterStudio(true)}>
                          + Create New Character
                        </button>
                      </div>
                    )}
                    {phasePlanner.phase_media[phase].mediaType === 'ai_image' && (
                      <div className="phase-media-prompt">
                        <select
                          value={DEFAULT_AI_PROMPTS.some(p => p.id === phasePlanner.phase_media[phase].prompt) ? phasePlanner.phase_media[phase].prompt : 'custom'}
                          onChange={e => e.target.value === 'custom' ? setPhasePrompt(phase, '') : setPhasePrompt(phase, e.target.value)}
                        >
                          {DEFAULT_AI_PROMPTS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                          <option value="custom">➕ Custom Prompt</option>
                        </select>
                        {!DEFAULT_AI_PROMPTS.some(p => p.id === phasePlanner.phase_media[phase].prompt) && (
                          <textarea
                            placeholder="Describe your custom AI image prompt..."
                            rows={3}
                            value={phasePlanner.phase_media[phase].prompt || ''}
                            onChange={e => setPhasePrompt(phase, e.target.value)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="phase-planner-info">
                <strong>Intro</strong> packs segments from the start until it hits the intro minute mark.{' '}
                <strong>Finale</strong> packs segments from the end.{' '}
                <strong>Body</strong> fills whatever remains in the middle.
              </div>
            </div>
          ) : (
            <div className="phase-planner-collapsed">
              <span className="phase-summary-text">{phaseSummary}</span>
            </div>
          )}

          {/* ── STICKY HEADER ─────────────────────────────────── */}
          <div className="composition-header">
            <div className="feature-toggles">
              <button
                className={`feature-pill ${kenBurns ? 'feature-pill--on' : ''}`}
                onClick={handleKenBurns}
                title="Ken Burns motion effect on images"
              >
                <FaMagic className="pill-icon" />
                <span>Ken Burns</span>
                {kenBurns && <span className="pill-dot" />}
              </button>

              <button
                className={`feature-pill ${storyboardAnimation ? 'feature-pill--on' : ''}`}
                onClick={handleStoryboard}
                title="Storyboard animation"
              >
                <FaLayerGroup className="pill-icon" />
                <span>Storyboard</span>
                {storyboardAnimation && <span className="pill-dot" />}
              </button>

              <button
                className={`feature-pill ${transitions ? 'feature-pill--on' : ''}`}
                onClick={() => setTransitions(p => !p)}
                title="Enable segment transitions"
              >
                <MdTransform className="pill-icon" />
                <span>Transitions</span>
                {transitions && <span className="pill-dot" />}
              </button>

              <div className="pill-divider" />
              <button
                className={`feature-pill feature-pill--talking ${useTalkingHeads ? 'feature-pill--on feature-pill--talking-on' : ''}`}
                onClick={() => setUseTalkingHeads(p => !p)}
                title="Export audio for DreamFace talking head generation"
              >
                <MdFaceRetouchingNatural className="pill-icon" />
                <span>Talking Heads</span>
                {useTalkingHeads && <span className="pill-dot" />}
              </button>
            </div>

            <div className="composition-btns">
              <button className="btn-save" onClick={updateSectionData} disabled={loading}>
                {loading ? 'Saving…' : 'Save Changes'}
              </button>
              <button className="btn-planner" onClick={() => setMediaPlannerOpen(v => !v)}>
                {mediaPlannerOpen ? 'Hide' : 'Show'} Planner
              </button>
              <button
                className="btn-generate"
                onClick={() => {
                  if (!runPreGenerateChecks()) return;
                  setChooseChannel(true);
                }}
                disabled={loading}
              >
                {loading ? 'Processing…' : useTalkingHeads ? '🎙 Export Audio' : 'Generate Video'}
              </button>
            </div>
          </div>

          {useTalkingHeads && (
            <div className="talking-heads-banner">
              <MdFaceRetouchingNatural className="banner-icon" />
              <div className="banner-text">
                <strong>Talking Heads mode active</strong>
                <span>Audio will be exported as batches (max 9 min each). You'll receive download links via Telegram to process in DreamFace, then submit avatar links to resume video generation.</span>
              </div>
            </div>
          )}

          {/* ── IMAGES ───────────────────────────────────────── */}
          {active === 'images' && (
            <>
              <NarrationImagePanel
                narrationImageMode={narrationImageMode}
                setNarrationImageMode={setNarrationImageMode}
                globalNarrationImage={globalNarrationImage}
                setGlobalNarrationImage={setGlobalNarrationImage}
                segmentNarrationImages={segmentNarrationImages}
                setSegmentNarrationImages={setSegmentNarrationImages}
                currentSection={currentSection}
                sectionName={sectionName}
                userToken={user?.token}
              />

              <div className="media">
                {narrationImageMode === 'global' && globalNarrationImage && (
                  <div className="teaser-studio__hint" style={{ padding: '0 0 12px' }}>
                    Global narration image is active — character image pools below are ignored for narration segments.
                  </div>
                )}

                {currentSection?.section_images.map((imageData, index) => (
                  <div key={index}>
                    <div className="character-link-container">
                      <div className="charcter-link">
                        <FaGlobe />
                        <a href={`https://www.google.com/search?q=${imageData.character}`} target="_blank" rel="noreferrer">
                          {imageData.character}
                        </a>
                      </div>
                    </div>
                    <ImageGrid
                      images={imageData.images || []}
                      selectedData={selectedData}
                      sectionName={sectionName}
                      character={imageData.character}
                      handleImageSelect={handleImageSelect}
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── INTRO TEASER ─────────────────────────────────── */}
          {active === 'teaser' && (
            <IntroTeaserPanel
              teaserConfig={introTeaserConfig}
              setTeaserConfig={setIntroTeaserConfig}
              presetId={teaserPresetId}
              setPresetId={setTeaserPresetId}
              introTagActive={introTagActive}
              orientation={orientation}
              user={user}
              userToken={user?.token}
            />
          )}

          {active === 'banner' && (
            <TransitionBannerPanel
              bannerEnabled={transitionBannerEnabled}
              setBannerEnabled={setTransitionBannerEnabled}
              bannerConfig={transitionBannerConfig}
              setBannerConfig={setTransitionBannerConfig}
              presetId={bannerPresetId}
              setPresetId={setBannerPresetId}
              orientation={orientation}
              user={user}
              userToken={user?.token}
            />
          )}

          {/* ── VIDEOS: canvas switch ────────────────────────── */}
          {active === 'videos' && (
            <div className="canvas-switch-bar">
              <button
                type="button"
                role="switch"
                aria-checked={canvasMode && !useTalkingHeads}
                className="canvas-switch"
                disabled={useTalkingHeads}
                onClick={() => setCanvasMode(v => !v)}
                title={useTalkingHeads
                  ? 'Not used with Talking Heads'
                  : canvasMode ? 'Canvas on — turn off to edit clips' : 'Design a narration canvas and a clip canvas'}
              >
                Canvas
                <span className="canvas-switch__track" aria-hidden="true" />
              </button>
            </div>
          )}

          {active === 'videos' && canvasMode && !useTalkingHeads && (
            <CanvasStudio
              user={user}
              orientation={orientation}
              configs={canvasConfigs}
              onConfigChange={handleCanvasConfig}
              channelId={channelData?._id}
            />
          )}

          {/* ── VIDEOS ───────────────────────────────────────── */}
          {active === 'videos' && !(canvasMode && !useTalkingHeads) && selectedMedia.url && (() => {
            const charClips = selectedData[sectionName]?.clips?.find(c => c.characterName === selectedMedia.character);
            const vidIdx = charClips?.videos?.findIndex(v => v.url === selectedMedia.url) ?? -1;
            const vidData = vidIdx >= 0 ? charClips.videos[vidIdx] : null;

            return (
              <div className="composition-videos">
                <div className="video-section">
                  <Video
                    ref={videoRef}
                    videoUrl={selectedMedia.url}
                    onClipSave={onClipSave}
                    character={selectedMedia.character}
                    onPhaseChange={setIsSavingStart}
                    clips={vidData?.vidclips || []}
                    onCropApply={(cropSpec, targets) => applyCropToClips(selectedMedia.character, selectedMedia.url, cropSpec, targets)}
                    clipEditTarget={clipEditTarget}
                    onApplyClipTimeFromVideo={applyClipTimeFromVideo}
                    onClipEditPlayhead={handleClipEditPlayhead}
                    onCancelClipEdit={handleCancelClipField}
                    onClipRangeEnd={handleClipRangeEnd}
                    onDurationReady={handleDurationReady}
                  />
                </div>

                <div
                  className="clip-data-section"
                  onClick={e => e.stopPropagation()}
                  onTouchEnd={e => e.stopPropagation()}
                >
                  <VideoClipsPanel
                    clips={vidData?.vidclips || []}
                    editHints={sectionName === 'intro' ? editHints : null}
                    previewingClipIndex={previewingClipIndex}
                    onPreviewClip={handlePreviewClip}
                    onUseAll={handleUseAll}
                    activeField={clipEditTarget}
                    onActivateField={handleActivateClipField}
                    onSaveField={handleSaveClipField}
                    onCancelField={handleCancelClipField}
                    onUpdateClipTime={(clipIdx, field, val) => {
                      if (vidIdx < 0) return;
                      updateClipTime(selectedMedia.character, selectedMedia.url, vidIdx, clipIdx, field, val);
                    }}
                    onEditCrop={clipIdx => {
                      if (!videoRef.current?.openCrop?.({ clipIndices: [clipIdx] })) {
                        toast.info('Cropping works on YouTube and direct video files only.');
                      }
                    }}
                    onRemoveCrop={clipIdx => applyCropToClips(selectedMedia.character, selectedMedia.url, null, [clipIdx])}
                    onDeleteClip={clipIdx => {
                      if (vidIdx < 0) return;
                      setClipEditTarget(null);
                      deleteClip(selectedMedia.character, selectedMedia.url, vidIdx, clipIdx);
                    }}
                    onSeek={sectionName === 'intro' && editHints?.timebase
                      ? handleSeekFromCutHint
                      : handleSeekToTimestamp}
                    onNudgeClipTime={handleNudgeClipTime}
                    onMuteClip={clipIdx => {
                      if (vidIdx < 0) return;
                      muteClip(selectedMedia.character, selectedMedia.url, vidIdx, clipIdx);
                    }}
                    onEffectChange={(clipIdx, effect) => {
                      const fakeEvent = { target: { value: effect } };
                      handleAddEffect(selectedMedia.character, selectedMedia.url, clipIdx, fakeEvent);
                    }}
                    showMute
                    showEffect
                    showTag
                    clipTagToggles={activeClipTagToggles}
                    onClipTagToggle={clipIdx => {
                      handleClipToggle(clipIdx);
                    }}
                    onClipTagChange={(clipIdx, tag) => {
                      const fakeEvent = { target: { value: tag } };
                      handleAddClipTag(selectedMedia.character, selectedMedia.url, clipIdx, fakeEvent);
                    }}
                    manualInput={manualInput}
                    onManualInputChange={handleManualChange}
                    isSavingStart={isSavingStart}
                    onManualSubmit={handleManualSubmit}
                    onCopyClips={vidData ? () => copyClipObjectToClipboard(vidData) : undefined}
                  />
                </div>
              </div>
            );
          })()}

          {/* ── SCRIPTS ──────────────────────────────────────── */}
          {active === 'scripts' && (
            <div className="script">
              <textarea
                value={currentSection?.section_script || ''}
                onChange={handleScriptChange}
                spellCheck={false}
                placeholder="Section script will appear here…"
              />
            </div>
          )}

          {/* ── TIMELINE ─────────────────────────────────────── */}
          {active === 'timeline' && (
            <div className="timeline">
              {sectionName === 'intro' && (
                <CompositionJsonImport
                  busy={compositionImportBusy}
                  onImport={handleCompositionJsonImport}
                  open={compositionImportOpen}
                  onOpenChange={setCompositionImportOpen}
                />
              )}
              <BulkClipLinkImport
                characters={currentSection?.section_characters || []}
                defaultCharacter={currentSection?.section_characters?.[0] || ''}
                segments={currentSection?.segment || []}
                busy={bulkImportBusy}
                onImport={handleBulkClipLinkImport}
                open={bulkImportOpen}
                onOpenChange={setBulkImportOpen}
                placement={bulkImportPlacement}
                onPlacementChange={setBulkImportPlacement}
              />
              {(() => {
                const clipSuggestions = getClipSegmentSuggestions(
                  selectedData,
                  sectionName,
                  currentSection?.segment,
                );
                const segmentsList = currentSection?.segment || [];
                // every [CLIP:tag] segment, with its clip (if one carries that tag) and play range
                const clipSegs = segmentsList.map((seg, i) => {
                  const tag = (seg.tag || extractClipTagFromText(seg.text || '')).trim();
                  const isClip = isClipSegmentText(seg.text || '');
                  const loc = tag ? findClipByTag(selectedData, sectionName, tag, editHints?.timebase) : null;
                  if (!isClip && !loc) return null;
                  const hint = sectionName === 'intro' && editHints && tag ? clipHintForTag(editHints, tag) : null;
                  const range = previewRangeForClip(loc?.clip, hint);
                  const playable = !!loc && !!clipRangeSeconds(range);
                  return { index: i, tag, loc, range, playable };
                }).filter(Boolean);
                const playableSegs = clipSegs.filter(c => c.playable);
                const missingCount = clipSegs.filter(c => !c.loc).length;
                const neighbour = (index, dir) => {
                  const pos = playableSegs.findIndex(c => c.index === index);
                  return playableSegs[pos + dir] || null;
                };
                const startPlayAll = () => {
                  if (!playableSegs.length) return;
                  setTimelinePreview({ index: playableSegs[0].index, playAll: true });
                };

                return (
                <>
                {clipSegs.length > 0 && (
                  <div className="tlp-bar" role="toolbar" aria-label="Clip previews">
                    <span><strong>{clipSegs.length}</strong> clip segment{clipSegs.length === 1 ? '' : 's'}</span>
                    {missingCount > 0 && (
                      <span className="tlp-bar__warn">· {missingCount} without a tagged clip</span>
                    )}
                    <span className="tlp-bar__spacer" />
                    {timelinePreview?.playAll ? (
                      <button type="button" className="tlp-btn tlp-btn--active" onClick={() => setTimelinePreview(null)}>
                        Stop playing all
                      </button>
                    ) : (
                      <button type="button" className="tlp-btn tlp-btn--primary" onClick={startPlayAll} disabled={!playableSegs.length}>
                        ▶ Play all clips in order
                      </button>
                    )}
                  </div>
                )}
                {segmentsList.map((segment, index) => {
                const segTag = (segment.tag || extractClipTagFromText(segment.text || '')).trim();
                const segHint = sectionName === 'intro' && editHints && segTag
                  ? clipHintForTag(editHints, segTag)
                  : null;
                const segHintOpen = timelineHintOpenIndex === index;
                const segTimelineCuts = segHint ? timelineCutsForTag(editHints, segTag) : [];
                const segClipLoc = segTag
                  ? findClipByTag(selectedData, sectionName, segTag, editHints?.timebase)
                  : null;
                const clipSeg = clipSegs.find(c => c.index === index) || null;
                const previewOpen = timelinePreview?.index === index;
                const prevSeg = previewOpen ? neighbour(index, -1) : null;
                const nextSeg = previewOpen ? neighbour(index, 1) : null;
                const posInList = playableSegs.findIndex(c => c.index === index);

                return (
                <div
                  key={index}
                  className={`segment ${segHintOpen ? 'segment--hint-open' : ''} ${previewOpen ? 'segment--previewing' : ''}`}
                >
                  <div className="segment-head">
                    <select
                      value={segment.character}
                      onChange={e => {
                        setSections(prev => {
                          const updated = [...prev];
                          updated[choosenSection - 1].segment[index].character = e.target.value;
                          return updated;
                        });
                      }}
                    >
                      <option value={segment.character}>{segment.character || '— select —'}</option>
                      {selectedData[sectionName]?.section_characters.map((char, idx) => (
                        <option key={idx} value={char}>{char}</option>
                      ))}
                    </select>

                    <label>
                      <input className="toggle-checkbox" type="checkbox"
                        checked={isChecked[index]} onChange={() => handleToggle(index)} />
                      <div className="toggle-slot">
                        <div className="sun-icon-wrapper" />
                        <div className="toggle-button" />
                        <div className="moon-icon-wrapper" />
                      </div>
                    </label>

                    {isChecked[index] && (
                      <div className="segment-tag-input">
                        <div className="tag-input">
                          <input className="to-do-input" placeholder="Tag this segment…"
                            type="text" value={segment?.tag || ''}
                            onChange={e => handleAddTag(index, e)} />
                          <div className="todo-line" />
                        </div>
                      </div>
                    )}

                    {segTag && (
                      <span className="segment__tag-badge">{segTag}</span>
                    )}
                    {segHint && (
                      <button
                        type="button"
                        className={`clip-card__hint-badge segment__hint-btn ${segHintOpen ? 'clip-card__hint-badge--open' : ''}`}
                        onClick={() => setTimelineHintOpenIndex(segHintOpen ? null : index)}
                        aria-expanded={segHintOpen}
                      >
                        Hint
                      </button>
                    )}
                  </div>

                  {clipSeg && (
                    <ClipStrip
                      tag={clipSeg.tag}
                      loc={clipSeg.loc}
                      range={clipSeg.range}
                      open={previewOpen}
                      playingAll={previewOpen && timelinePreview?.playAll}
                      onPreview={() => setTimelinePreview(previewOpen ? null : { index, playAll: false })}
                      onOpenInVideos={() => openClipInVideos(clipSeg.loc)}
                    />
                  )}

                  {clipSeg && previewOpen && (
                    <TimelineClipPreview
                      key={`${index}:${clipSeg.loc?.url}:${clipSeg.range?.start}:${clipSeg.range?.end}`}
                      loc={clipSeg.loc}
                      range={clipSeg.range}
                      tag={clipSeg.tag}
                      autoPlay
                      playAll={!!timelinePreview?.playAll}
                      position={posInList >= 0 ? `${posInList + 1} / ${playableSegs.length}` : ''}
                      hasPrev={!!prevSeg}
                      hasNext={!!nextSeg}
                      onPrev={() => prevSeg && setTimelinePreview({ index: prevSeg.index, playAll: !!timelinePreview?.playAll })}
                      onNext={() => nextSeg && setTimelinePreview({ index: nextSeg.index, playAll: !!timelinePreview?.playAll })}
                      onEnded={() => {
                        if (timelinePreview?.playAll) {
                          if (nextSeg) setTimelinePreview({ index: nextSeg.index, playAll: true });
                          else setTimelinePreview({ index, playAll: false });
                        }
                      }}
                      onTogglePlayAll={() => setTimelinePreview({ index, playAll: !timelinePreview?.playAll })}
                      onClose={() => setTimelinePreview(null)}
                      onOpenInVideos={() => openClipInVideos(clipSeg.loc)}
                    />
                  )}

                  {segHint && (
                    <ClipHintDrawer
                      open={segHintOpen}
                      onClose={() => setTimelineHintOpenIndex(null)}
                      cutHint={segHint}
                      tag={segTag}
                      timelineCuts={segTimelineCuts}
                      clipIndex={segClipLoc?.clipIndex ?? null}
                      clipStartTime={segClipLoc?.clip?.startTime}
                      clipEndTime={segClipLoc?.clip?.endTime}
                      activeField={clipEditTarget}
                      onActivateField={(clipIdx, field) => {
                        handleActivateClipFieldForTag(segTag, field);
                      }}
                      onSaveField={handleSaveClipField}
                      onCancelField={handleCancelClipField}
                      onSeek={handleSeekFromCutHint}
                      onNudgeTime={(clipIdx, field, delta) => {
                        focusVideosClipByTag(segTag, () => {
                          handleNudgeClipTime(clipIdx, field, delta);
                        });
                      }}
                    />
                  )}

                  <textarea
                    value={segment.text}
                    onChange={e => {
                      setSections(prev => {
                        const updated = [...prev];
                        updated[choosenSection - 1].segment[index].text = e.target.value;
                        updated[choosenSection - 1].section_script = updated[choosenSection - 1].segment.map(s => s.text).join(' ');
                        return updated;
                      });
                    }}
                    placeholder="Segment text…"
                  />

                  <div className="segment-controls">
                    <div className="control-button" onClick={() => deleteSegment(index)}><FaTrash className="icon delete-icon" /></div>
                    <BulkClipLinkAnchorButton
                      index={index}
                      mode="before"
                      disabled={bulkImportBusy}
                      onActivate={(p) => {
                        setBulkImportPlacement(p);
                        setBulkImportOpen(true);
                      }}
                    />
                    <BulkClipLinkAnchorButton
                      index={index}
                      mode="after"
                      disabled={bulkImportBusy}
                      onActivate={(p) => {
                        setBulkImportPlacement(p);
                        setBulkImportOpen(true);
                      }}
                    />
                    <SegmentAddMenu
                      suggestions={clipSuggestions}
                      onAddBlank={() => addBlankSegment(index)}
                      onAddClipTag={(tag) => addClipSegmentByTag(index, tag)}
                      onAddGenericClip={() => addGenericClipSegment(index)}
                      showBlankOption
                      showGenericClipOption
                    />
                  </div>
                </div>
                );
              })}
                </>
                );
              })()}
            </div>
          )}

          {/* ── CHARACTERS ───────────────────────────────────── */}
          {active === 'characters' && (
            <div className="characters">
              {currentSection?.section_characters?.length > 0 ? (
                currentSection.section_characters.map((character, index) => (
                  <div key={index} className="character-card">
                    <input type="text" value={character} className="character-input"
                      placeholder="Character name…"
                      onChange={e => handleCharacterChange(index, e.target.value)} />
                    <div className="character-controls">
                      <div className="control-button" onClick={() => removeCharacter(index)}><FaTrash className="icon delete-icon" /></div>
                      <div className="control-button" onClick={() => addCharacter()}><FaPlus className="icon add-icon" /></div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-character">
                  <button className="add-character-btn" onClick={() => addCharacter()}><FaPlus /> Add Character</button>
                </div>
              )}
            </div>
          )}

          {/* ── CAPTIONS ─────────────────────────────────────── */}
          <div className="captions-tab">
            <div className="captions-scroll">
              {active === 'captions' && (
                <CaptionEditor
                  captionsEnabled={captionsEnabled}
                  captionSettings={captionSettings}
                  onSettingsChange={setCaptionSettings}
                  onEnabledChange={setCaptionsEnabled}
                />
              )}
            </div>
          </div>

          {/* ── BG MUSIC ─────────────────────────────────────── */}
          {active === 'bgMusic' && (
            <BgMusic selectedMusic={bgMusic} onSelectMusic={setBgMusic} />
          )}
        </div>

        {/* ─── ADD IMAGE OVERLAY ────────────────────────────── */}
        {addMedia === 'image' && (
          <div className="add-media">
            <div className="cancel-add-media" onClick={() => setAddMedia('')}><FaTimes /></div>
            <div className="form-container">
              <form className="form" onSubmit={manualImageAdd}>
                <div className="form-group">
                  <label>Character</label>
                  <select value={addImageData.character} onChange={e => setAddImageData(p => ({ ...p, character: e.target.value }))}>
                    {currentSection?.section_characters.map((char, idx) => <option key={idx} value={char}>{char}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select value={addImageData.type || ''} onChange={e => setAddImageData(p => ({ ...p, type: e.target.value }))}>
                    <option value="">Select type…</option>
                    <option value="single-image">Single Image</option>
                    <option value="multiple-image">Image Pull (Google)</option>
                    <option value="bulk-image">Bulk URLs</option>
                  </select>
                </div>
                {addImageData.type === 'single-image' && (
                  <div className="form-group">
                    <label>Image URL</label>
                    <input type="text" placeholder="https://…" value={addImageData.url} onChange={e => setAddImageData(p => ({ ...p, url: e.target.value }))} />
                  </div>
                )}
                {addImageData.type === 'multiple-image' && (
                  <>
                    <div className="form-group">
                      <label>Search Keyword</label>
                      <input type="text" placeholder="e.g. elon musk" value={addImageData.keyword} onChange={e => setAddImageData(p => ({ ...p, keyword: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Amount</label>
                      <select value={addImageData.number || ''} onChange={e => setAddImageData(p => ({ ...p, number: e.target.value }))}>
                        <option value="">Select…</option>
                        <option value={10}>10</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </>
                )}
                {addImageData.type === 'bulk-image' && (
                  <div className="form-group">
                    <label>URLs (comma-separated)</label>
                    <textarea rows={5} placeholder="https://img1.jpg, https://img2.jpg, …" value={addImageData.bulkUrls} onChange={e => setAddImageData(p => ({ ...p, bulkUrls: e.target.value }))} />
                  </div>
                )}
                <button className="form-submit-btn" type="submit">Add Images</button>
              </form>
            </div>
          </div>
        )}

        {/* ─── ADD VIDEO OVERLAY ────────────────────────────── */}
        {addMedia === 'video' && (
          <div className="add-media">
            <div className="cancel-add-media" onClick={() => setAddMedia('')}><FaTimes /></div>
            <div className="form-container">
              <form className="form" onSubmit={manualVideoAdd}>
                <div className="form-group">
                  <label>Input Method</label>
                  <select value={videoAddAction} onChange={e => setVideoAddAction(e.target.value)}>
                    <option value="video">URL Entry</option>
                    <option value="videoData">Paste Clip JSON</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Character</label>
                  <select value={addVideoData.character} onChange={e => setAddVideoData(p => ({ ...p, character: e.target.value }))}>
                    {selectedData[sectionName]?.section_characters.map((char, idx) => <option key={idx} value={char}>{char}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Video Title</label>
                  <input type="text" placeholder="Descriptive title…" value={addVideoData.title} onChange={e => setAddVideoData(p => ({ ...p, title: e.target.value }))} />
                </div>
                {videoAddAction === 'video' && (
                  <div className="form-group">
                    <label>Video URL</label>
                    <input type="text" placeholder="YouTube, direct .mp4, or page URL (e.g. Grok)…" value={addVideoData.url} onChange={e => setAddVideoData(p => ({ ...p, url: e.target.value }))} />
                  </div>
                )}
                {videoAddAction === 'videoData' && (
                  <div className="form-group">
                    <label>Clip JSON</label>
                    <textarea rows={6} placeholder='{"url": "…", "vidclips": […]}' value={addVideoData.clipJson || ''} onChange={e => setAddVideoData(p => ({ ...p, clipJson: e.target.value }))} />
                  </div>
                )}
                <div className="form-submit-row">
                  <button className="form-submit-btn" type="submit">Add Video</button>
                  {videoAddAction === 'video' && (
                    <button
                      className="form-submit-btn form-submit-btn--use-all"
                      type="button"
                      onClick={(e) => manualVideoAdd(e, { useAll: true })}
                    >
                      Use all
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

        {showConfirmation && (
          <ConfirmationBox
            message={useTalkingHeads
              ? "Export audio batches for DreamFace? You'll receive download links via Telegram."
              : "Generate this video now? This may take a few minutes."}
            handleConfirm={handleConfirm}
          />
        )}

        {loading && <Loader />}
      </div>

      {/* ─── CHANNEL SELECT ──────────────────────────────────── */}
      {chooseChannel && (
        <div className="composition-channel">
          <div className="get-channel">
            <GetChannel setChannelData={setChannelData} />
            <div className="composition-channel-btns">
              <button onClick={() => {
                if (!runPreGenerateChecks()) return;
                setChooseChannel(false);
                setShowConfirmation(true);
              }}>Proceed</button>
              <button onClick={() => setChooseChannel(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── CHARACTER STUDIO MODAL ──────────────────────────── */}
      {showCharacterStudio && (
        <div className="character-studio-modal">
          <div className="character-studio-container">
            <div className="modal-header">
              <h2>Character Studio</h2>
              <button className="close-modal-btn" onClick={() => setShowCharacterStudio(false)}><FaTimes /></button>
            </div>
            <CharacterStudio onComplete={() => setShowCharacterStudio(false)} />
          </div>
        </div>
      )}
    </>
  );
}

export default Composition;